<template>
  <div
    :class="props.fullScreen ? 'animation-container-fill' : 'animation-container'"
    ref="container"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

// Types
import type { Ref } from 'vue'
import type { AudioPlayer } from '@/customTypes'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useEffectsStore } from '@/stores/EffectsStore'

// Plugins
import lottie from 'lottie-web'

// Services
import Logger from '@/services/Logger.js'

const logger: any = new Logger('SceneAnimation', true)

const emit = defineEmits<{
  (e: 'animation-finished'): void
}>()

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  fullScreen: {
    type: Boolean,
    default: false
  },
  audioTrack: {
    type: Boolean,
    default: false
  }
})

const dataStore = useDataStore()
const { screenerTemplateId } = storeToRefs(dataStore)
const soundEffectsStore = useSoundEffectsStore()
const effectsStore = useEffectsStore()

const animationData = ref([])
const container: Ref<HTMLDivElement | null> = ref(null)
const animationInstance: any = ref(null)
const audioPlayer: Ref<AudioPlayer | undefined> = ref(undefined)

// NOTE: Lifecycle
onMounted(async () => {
  await fetchSceneAnimation()
  animationInstance.value = lottie.loadAnimation({
    container: container.value as HTMLDivElement,
    loop: false,
    autoplay: false,
    animationData: animationData.value,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  })
})

onUnmounted(() => {
  animationInstance.value.destroy()
  animationInstance.value = null
})

// NOTE: Methods
const fetchSceneAnimation = async () => {
  const response = await fetch(
    `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/animations/${props.name}.json?v=${_ASSETS_VERSION_}`
  )
  if (!response.ok) return
  const data = await response.json()
  animationData.value = data
  const animationInfo = effectsStore.getAnimation(props.name, 'scene')
  if (animationInfo?.audio) await setupSoundEffects()
}

const setupSoundEffects = async () => {
  try {
    audioPlayer.value = await soundEffectsStore.getAudioPlayer(props.name)
    //audioPlayer.value?.setVolume(0.01)
  } catch (error) {
    logger.error('Error setting up sound effects for scene:', error)
  }
}

const onAnimationComplete = () => {
  animationInstance.value.removeEventListener('complete', onAnimationComplete)
  emit('animation-finished')
}

const play = () => {
  if (animationInstance.value) {
    animationInstance.value.goToAndStop(0)
    animationInstance.value.play()
    animationInstance.value.addEventListener('complete', onAnimationComplete)
    if (audioPlayer.value) audioPlayer.value.start()
  }
}

defineExpose({
  play
})
</script>
<style scoped>
.animation-container {
  position: absolute;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
.animation-container-fill {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
</style>
